import get from 'lodash.get';
import React, { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { REFUND_ASSIST_VIEW_ID } from '../../../shipping-grid/views/view-id-constants';
import { useSelectedShippingViewId } from '../../../../pages/shipping-page/shipping-left-nav/shipping-left-nav-context';
import { FulfillmentPlanService } from '../../../../services/fulfillment-plan-service';
import { OtherShippingOptionsService } from '../../../../services/other-shipping-options/other-shipping-options-service';
import Global from '../../../../store/global-selectors';
import { launchVoidModal } from '../../../../store/labels/labels.actions';
import * as MarkAsShippedActions from '../../../../store/mark-as-shipped/mark-as-shipped.actions';
import { markLabelPrinted, markPackingSlipPrinted, printPackingSlip } from '../../../../store/printing/print.actions';
import * as SendNotificationsActions from '../../../../store/send-notifications/send-notifications.actions';
import { printPreview, validateShipmentDetailsShipToAddress } from '../../../../store/shipments/shipment-details/shipments.details.actions';
import getDisplayInformation from '../../../data-driven-logo/get-display-information';

import { ShipmentDetailsModalContent } from './index';

export const ShipmentDetailsModalContentContainer = ({ fulfillmentPlanId, shipmentDetailModalId, closeModal }) => {
  const dispatch = useDispatch();
  const selectedViewId = useSelectedShippingViewId();
  const isRefundAssistView = selectedViewId === REFUND_ASSIST_VIEW_ID;

  // State selectors
  const shipmentDetails = useSelector((state) =>
    Global.selectShipmentDetailsShipmentDetailsByShipmentDetailModalId(state, { shipmentDetailModalId }),
  );
  const isLoading = get(shipmentDetails, 'isLoading', true);

  const baseFulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId })) ?? emptyObject;

  const serviceReferenceLookup = useSelector(Global.selectReferenceDatagetServiceReferenceLookup);
  const selectedService = baseFulfillmentPlan.options?.serviceId ? serviceReferenceLookup[baseFulfillmentPlan.options.serviceId] : null;

  const carriers = useSelector(Global.selectReferenceDatagetSellerCarriers);
  const sellerActiveFulfillmentProviders = useSelector(Global.selectSellerActiveFulfillmentProviders);
  const isShipmentFromCustomerDetails = useSelector(Global.selectShipmentDetailsShipmentFromCustomerDetails);

  // Memoized computed values
  const fulfillmentPlan = useMemo(() => {
    if (!baseFulfillmentPlan.options) return baseFulfillmentPlan;

    return {
      ...baseFulfillmentPlan,
      options: {
        ...baseFulfillmentPlan.options,
        confirmation: OtherShippingOptionsService.getConfirmationText(baseFulfillmentPlan, carriers, selectedService),
      },
    };
  }, [baseFulfillmentPlan, carriers, selectedService]);

  const fulfillments =
    useSelector((state) =>
      fulfillmentPlan.fulfillmentIds
        ? Global.selectEntitiesFulfillmentsByIds(state, { fulfillmentIds: fulfillmentPlan.fulfillmentIds })
        : emptyArray,
    ) ?? emptyArray;

  const salesOrders =
    useSelector((state) => Global.selectEntitiesSalesOrdersByFulfillmentPlanIds(state, { fulfillmentPlanIds: [fulfillmentPlanId] })) ??
    emptyArray;

  const activeFulfillment = useMemo(
    () => fulfillments.find((f) => f.fulfillmentPlanId === fulfillmentPlanId && !f.isLabelVoided),
    [fulfillments, fulfillmentPlanId],
  );

  const hasActiveLabel = !!activeFulfillment;

  const activeLabelProps = useMemo(() => {
    if (!hasActiveLabel) return emptyObject;

    const serviceId = get(activeFulfillment, 'serviceId');
    const service = serviceReferenceLookup[serviceId];
    const displayInformation = getDisplayInformation({
      providerId: get(activeFulfillment, 'providerId'),
    });
    const defaultServiceName = get(displayInformation, 'displayName', 'Unknown Service');
    const serviceName = service ? service.name : defaultServiceName;

    return {
      serviceName,
      displayInformation,
    };
  }, [hasActiveLabel, activeFulfillment, serviceReferenceLookup]);

  const shipFromData = useSelector((state) => Global.selectSellerShipFromsById(state, { shipFromId: fulfillmentPlan.shipFromId }));

  const shipFrom = useMemo(() => {
    const isExternal = FulfillmentPlanService.isExternal(fulfillmentPlan);
    if (isExternal) {
      return FulfillmentPlanService.getExternalShipFromName(fulfillmentPlan, sellerActiveFulfillmentProviders);
    }
    return get(shipFromData, 'name');
  }, [fulfillmentPlan, sellerActiveFulfillmentProviders, shipFromData]);

  const packageType = useSelector((state) => get(Global.selectSellerPackageType(state, fulfillmentPlan), 'name'));

  // Action creators
  const actions = useMemo(
    () => ({
      printPackingSlip: (fulfillmentPlans) => {
        const fulfillmentPlanIds = fulfillmentPlans.map((fill) => fill.fulfillmentPlanId);
        const hasPrintedPackingSlip = fulfillmentPlans.some((plan) => plan.packingSlipPrintedStatus.isPrinted);
        dispatch(printPackingSlip(fulfillmentPlanIds, hasPrintedPackingSlip));
      },
      undoMarkShipped: () => {
        dispatch(MarkAsShippedActions.undoMarkShipped([fulfillmentPlanId]));
      },
      printPreview: () => {
        dispatch(printPreview(shipmentDetailModalId));
      },
      packingSlipPrintAction: (fpid, isPrinted) => {
        dispatch(markPackingSlipPrinted([fpid], isPrinted));
      },
      labelPrintedAction: (fpid, fulfillmentId, isPrinted) => {
        dispatch(markLabelPrinted([fpid], [fulfillmentId], isPrinted));
      },
      sendNotifications: () => {
        dispatch(SendNotificationsActions.sendNotifications([fulfillmentPlanId]));
      },
      resendNotifications: () => {
        dispatch(SendNotificationsActions.retryNotifications([fulfillmentPlanId]));
      },
      validateAddress: (address) => dispatch(validateShipmentDetailsShipToAddress(address, fulfillmentPlanId)),
      launchVoidLabelModal: (fulfillment) => dispatch(launchVoidModal({ fulfillments: [fulfillment] })),
    }),
    [dispatch, fulfillmentPlanId, shipmentDetailModalId],
  );

  return (
    <ShipmentDetailsModalContent
      actions={actions}
      activeFulfillment={activeFulfillment}
      activeLabelProps={activeLabelProps}
      closeModal={closeModal}
      fulfillmentPlan={fulfillmentPlan}
      fulfillments={fulfillments}
      hasActiveLabel={hasActiveLabel}
      isLoading={isLoading}
      isRefundAssistView={isRefundAssistView}
      isShipmentFromCustomerDetails={isShipmentFromCustomerDetails}
      packageType={packageType}
      salesOrders={salesOrders}
      shipFrom={shipFrom}
      shipmentDetailModalId={shipmentDetailModalId}
    />
  );
};
